import {
  Component,
  computed,
  HostBinding,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { PAGE, PAGES } from '@constants/constants';
import {
  deleteDashboard,
  getDashboardsQuery,
  pinDashboard,
} from '@graphql/dashboard';
import { LayoutComponent } from '@layout/layout.component';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { OverlayService } from '@services/ui/overlay.service';
import { Apollo } from 'apollo-angular';
import {
  AlarmListQuery,
  AlarmListQueryVariables,
  DeleteDashboardMutation,
  DeleteDashboardMutationVariables,
  GetDashboardsQuery,
  GetDashboardsQueryVariables,
  PinDashboardMutation,
  PinDashboardMutationVariables,
} from 'gql/graphql';
import { Subscription } from 'rxjs';
import { DashboardModalComponent } from './dashboard-modal/dashboard-modal.component';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { DashboardEditComponent } from './dashboard-edit/dashboard-edit.component';
import { DashboardSidePanelComponent } from './dashboard-side-panel/dashboard-side-panel.component';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { LOADING } from '@utils/loader';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';
import { PermissionsService } from '@services/user/permissions.service';
import { URLS } from '@constants/urls';
import { DASHBOARDS_SUBTITLE } from './dashboards';
import { alarmListQuery } from '@graphql/alarm';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'abb-dashboards',
  standalone: true,
  template: `
    <page-layout [page]="PAGES.DASHBOARDS">
      <div title>
        @if (dashboards() && dashboards().length>0) {
        <div class="text-abb-primary font-semibold leading-none p-0 m-0">—</div>
        }
        <div class="flex items-center gap-x-3">
          @if (dashboards() && dashboards().length>0) {
          <div>{{ dashboard()?.name }}</div>
          <button
            mat-icon-button
            (click)="toggleMenu()"
            type="button"
            cdkOverlayOrigin
            #trigger="cdkOverlayOrigin"
            class="group"
          >
            <abb-icon
              class="group-aria-expanded:rotate-180"
              [name]="'mat/keyboard_arrow_down'"
            ></abb-icon>
          </button>
          <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="trigger"
            [cdkConnectedOverlayOpen]="showDashboardMenu()"
            [cdkConnectedOverlayHasBackdrop]="true"
            (backdropClick)="toggleMenu()"
            [cdkConnectedOverlayBackdropClass]="'opacity-0'"
          >
            <ul
              class="bg-abb-background rounded max-h-64 overflow-y-auto shadow divide-y divide-abb-black/[0.06]"
            >
              @for (d of dashboards(); track d.id) {
              <li
                [ngClass]="
                  dashboard()?.id === d.id ? 'bg-abb-primary-light/20' : ''
                "
                (click)="setDashboard(d); toggleMenu()"
                class="flex items-center justify-between p-4 py-2"
              >
                @if(canEdit()){
                <abb-button
                  variant="none"
                  [ngClass]="{
                    'text-abb-primary hover:text-abb-primary-light active:text-abb-primary-light':
                      d.pinned,
                    'text-abb-font/85 hover:text-abb-font/70 active:text-abb-font/70':
                      !d.pinned,
                    'transition-all mr-3': true,
                    'rotate-[45deg]': !d.pinned
                  }"
                  (click)="setPinned(d.id, !d.pinned)"
                  [icon]="d.pinned ? 'mat/push_pin' : 'mat-outlined/push_pin'"
                  [iconExternalSize]="d.pinned ? 'text-[17.5px]' : 'text-base'"
                  [matTooltip]="'Pin' | t"
                />
                }
                <span class="flex-1 cursor-pointer">{{ d.name }}</span>
                @if(canEdit()){
                <div class="flex items-center">
                  <abb-button
                    variant="none"
                    class="text-abb-font/85 hover:text-abb-font/70 active:text-abb-font/70 transition-all ml-6"
                    [ngClass]="{
                      'text-abb-primary hover:text-abb-primary-light active:text-abb-primary-light':
                        d.pinned,
                      'text-abb-font/85 hover:text-abb-font/70 active:text-abb-font/70':
                        !d.pinned,
                      'transition-all ml-6': true
                    }"
                    (click)="openEditModal(d, 'copy')"
                    [icon]="'mat/content_copy'"
                    iconSize="small"
                    [matTooltip]="'Clone' | t"
                  />
                  <abb-button
                    variant="none"
                    class="text-abb-font/85 hover:text-abb-font/70 active:text-abb-font/70 transition-all ml-2"
                    [ngClass]="{
                      'text-abb-primary hover:text-abb-primary-light active:text-abb-primary-light':
                        d.pinned,
                      'text-abb-font/85 hover:text-abb-font/70 active:text-abb-font/70':
                        !d.pinned,
                      'transition-all ml-2': true
                    }"
                    (click)="openEditModal(d, 'edit')"
                    [icon]="'abb/edit'"
                    iconSize="small"
                    [matTooltip]="'Edit' | t"
                  />
                  <abb-button
                    variant="none"
                    [ngClass]="{
                      'text-abb-primary hover:text-abb-primary-light active:text-abb-primary-light':
                        d.pinned,
                      'text-abb-font/85 hover:text-abb-font/70 active:text-abb-font/70':
                        !d.pinned,
                      'transition-all ml-2': true
                    }"
                    (click)="confirmDelete(d)"
                    [icon]="'abb/trash'"
                    iconSize="small"
                    [matTooltip]="'Delete' | t"
                  />
                </div>
                }
              </li>
              }
            </ul>
          </ng-template>
          } @if(canEdit()){
          <button
            (click)="openCreateModal()"
            class="flex text-xs items-center text-abb-primary gap-x-1 font-medium"
          >
            <abb-icon size="medium" name="mat-outlined/library_add"></abb-icon>
            {{ 'Create new dashboard' | t }}
          </button>
          }
        </div>
      </div>
      @if (editMode()) {
      <abb-button
        type="button"
        [variant]="'secondary'"
        (onclick)="leaveEditMode()"
        >{{ 'Stop editing' | t }}</abb-button
      >
      } @if (editMode()) {
      <abb-button type="button" (onclick)="openSidePanel()">{{
        'Add widget' | t
      }}</abb-button
      >} @if (dashboard(); as dashboard) {
      <abb-dashboard-edit
        [(editMode)]="editMode"
        [dashboard]="dashboard"
        (onEndUpdateWidget)="setDashboard($event)"
      ></abb-dashboard-edit>
      }
      <span
        subtitle
        [innerHTML]="subtitle().key | t : subtitle().params"
      ></span>
    </page-layout>
  `,
  styles: ``,
  imports: [
    LayoutComponent,
    TPipe,
    AbbIconComponent,
    AbbButtonComponent,
    DashboardEditComponent,
    OverlayModule,
    CommonModule,
    MatTooltipModule,
  ],
  host: { class: 'flex-1 flex flex-col pr-4 pb-4' },
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'dashboards',
    },
  ],
})
export class DashboardsComponent implements OnDestroy {
  @HostBinding('attr.id') componentid = 'dashboard-component';
  readonly PAGES: PAGES = PAGES;
  dashboards = signal<GetDashboardsQuery['getDashboards']>([]);
  public dashboard = signal<GetDashboardsQuery['getDashboards'][number] | null>(
    null
  );
  editMode = signal(false);
  showDashboardMenu = signal(false);
  loading = inject(LOADING);
  prefix = inject(TRANSLATION_PREFIX);
  canEdit = computed(() =>
    this.permissionsService.canEditPage(PAGES.DASHBOARDS)
  );
  subtitle = inject(DASHBOARDS_SUBTITLE);

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly overlay: OverlayService,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
    private readonly permissionsService: PermissionsService
  ) {
    this.subscriptions.push(
      apollo
        .watchQuery<GetDashboardsQuery, GetDashboardsQueryVariables>({
          query: getDashboardsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            const sortedDashboards = [...data.getDashboards].sort((a, b) => {
              const hasPinnedDashboards = data.getDashboards.some(
                (d) => d.pinned
              );

              if (hasPinnedDashboards) {
                if (a.pinned && !b.pinned) return -1;
                if (!a.pinned && b.pinned) return 1;
              }
              return a.name.localeCompare(b.name);
            });

            this.dashboards.set(sortedDashboards);
            if (!this.dashboard()) {
              this.dashboard.set(
                sortedDashboards.find((d) => d.pinned) ??
                  sortedDashboards.at(0) ??
                  null
              );
            } else {
              this.dashboard.set(
                sortedDashboards.find((d) => d.id === this.dashboard()?.id) ??
                  sortedDashboards.find((d) => d.pinned) ??
                  sortedDashboards.at(0) ??
                  null
              );
            }
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<AlarmListQuery, AlarmListQueryVariables>({
          query: alarmListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data, loading }) => {
            this.loading.set(loading);
            if (data.alarmList?.filter((el) => el.enabled).length > 0) {
              this.subtitle.set({
                key: 'You have {count} unread alerts',
                params: {
                  count: data.alarmList?.filter((el) => el.enabled).length,
                },
              });
            } else {
              this.subtitle.set({
                key: 'No alarms require your attention.',
                params: {},
              });
            }
          },
          error: (err) => {
            this.loading.set(false);
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  openCreateModal() {
    this.overlay.openModal(DashboardModalComponent, {
      mode: 'new',
      onSaved: (d) => {
        this.dashboard.set(d);
        this.enableEditMode();
        this.openSidePanel();
      },
    });
  }

  openEditModal(dashboard: GetDashboardsQuery['getDashboards'][number], mode: 'edit' | 'copy') {
    this.showDashboardMenu.set(false);
    this.overlay.openModal(DashboardModalComponent, {
      mode: mode,
      dashboard,
      onSaved: () => {},
    });
  }

  async confirmDelete(dashboard: GetDashboardsQuery['getDashboards'][number]) {
    this.showDashboardMenu.set(false);
    try {
      await this.overlay.openAlert({
        message: t(
          this.prefix,
          `Are you sure you want to delete "{name}"?`,
          this.translate,
          { name: dashboard.name }
        ),
        title: t(this.prefix, `Delete dashboard`, this.translate),
      });
    } catch (err) {
      // cancel
      return;
    }
    this.loading.set(true);
    this.apollo
      .mutate<DeleteDashboardMutation, DeleteDashboardMutationVariables>({
        mutation: deleteDashboard,
        variables: {
          dashboardId: dashboard.id,
        },
        awaitRefetchQueries: true,
      })
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.toast.notifySuccess(
            t(this.prefix, `Dashboard deleted successfully`, this.translate)
          );
          this.dashboards.set(
            this.dashboards().filter((d) => d.id !== dashboard.id)
          );
          if (this.dashboard()?.id === dashboard.id) {
            this.dashboard.set(this.dashboards().at(0) ?? null);
          }
        },
        error: () => {
          this.loading.set(false);
          this.toast.notifyError(
            t(
              this.prefix,
              `An error occurred while deleting the dashboard`,
              this.translate
            )
          );
        },
      });
  }

  toggleMenu() {
    this.showDashboardMenu.update((prev) => !prev);
  }

  setPinned(id: string, pinned: boolean) {
    this.loading.set(true);
    this.apollo
      .mutate<PinDashboardMutation, PinDashboardMutationVariables>({
        mutation: pinDashboard,
        variables: {
          dashboardId: id,
          pinned,
        },
      })
      .subscribe({
        next: () => {
          this.apollo.client
            .refetchQueries({
              include: ['GetDashboards'],
            })
            .finally(() => {
              this.loading.set(false);
            });
        },
        error: () => {
          this.loading.set(false);
          this.toast.notifyError(
            t(
              this.prefix,
              `An error occurred while pinning the dashboard`,
              this.translate
            )
          );
        },
      });
  }

  setDashboard(dashboard: GetDashboardsQuery['getDashboards'][number]) {
    this.dashboard.set(dashboard);
  }

  enableEditMode() {
    this.editMode.set(true);
  }

  leaveEditMode() {
    this.editMode.set(false);
  }

  openSidePanel() {
    const dashboard = this.dashboard();
    if (!dashboard) {
      return;
    }
    this.overlay.openSidePanel(DashboardSidePanelComponent, {
      dashboardId: dashboard.id,
      widgets: dashboard.widgets,
      mode: 'new',
    });
  }
}
